import React from 'react';
import styled from 'styled-components';

interface ButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'outline' | 'danger' | 'secondary';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
}

const StyledButton = styled.button<ButtonProps>`
  padding: ${props => 
    props.size === 'small' ? '0.5rem 1rem' :
    props.size === 'large' ? '0.875rem 2rem' :
    '0.625rem 1.5rem'
  };
  
  font-size: ${props =>
    props.size === 'small' ? '0.875rem' :
    props.size === 'large' ? '1rem' :
    '0.875rem'
  };
  
  font-weight: 600;
  border-radius: 8px;
  border: 2px solid;
  cursor: pointer;
  transition: all 0.2s ease;
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  
  @media (max-width: 768px) {
    padding: ${props => 
      props.size === 'small' ? '0.625rem 1rem' :
      props.size === 'large' ? '1rem 2rem' :
      '0.75rem 1.5rem'
    };
    
    font-size: ${props =>
      props.size === 'small' ? '0.875rem' :
      props.size === 'large' ? '1rem' :
      '0.9375rem'
    };
  }
  
  background: ${props => {
    if (props.variant === 'primary') return '#000000';
    if (props.variant === 'danger') return '#dc3545';
    if (props.variant === 'secondary') return '#6c757d';
    return 'transparent';
  }};
  
  color: ${props => {
    if (props.variant === 'outline') return '#000000';
    return '#ffffff';
  }};
  
  border-color: ${props => {
    if (props.variant === 'primary') return '#000000';
    if (props.variant === 'danger') return '#dc3545';
    if (props.variant === 'secondary') return '#6c757d';
    return '#000000';
  }};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    
    ${props => props.variant === 'outline' && `
      background: #000000;
      color: #ffffff;
    `}
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    &:hover:not(:disabled) {
      transform: none;
    }
    
    &:active:not(:disabled) {
      opacity: 0.8;
    }
  }
`;

const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  disabled = false,
  style,
}) => {
  return (
    <StyledButton
      onClick={onClick}
      type={type}
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      style={style}
    >
      {children}
    </StyledButton>
  );
};

export default Button;
